/**
 * Task 15 — synced entity registry for the remote adapter.
 * Append-only / immutable sets mirror docs/01-SCHEMA.sql + docs/08 §8–§9.
 */

import type { z } from 'zod';
import type { SyncedTableName } from '../schemas/local-only';
import {
  benefits,
  cardAutopayObservations,
  cardFeeSchedules,
  cardFinancialObservations,
  cardPayments,
  cards,
  cashReservations,
  deferredInterestExposures,
  evidenceSources,
  fieldAssertions,
  graceConfirmations,
  importBatches,
  merchantCategoryObservations,
  merchants,
  offers,
  paymentAllocations,
  paymentHistoryCoverage,
  purchaseQueries,
  recommendationResults,
  recurringCharges,
  reviewItems,
  rewardRules,
  settings,
  statementCycles,
  transactionSplits,
  transactions,
} from '../schemas/entities';

export type SyncedEntitySchemas = {
  remoteRow: z.ZodTypeAny;
  remoteInsertPayload: z.ZodTypeAny;
};

/** Append-only at the grant/policy level — insert only; never update/delete ops. */
export const APPEND_ONLY_TABLES: ReadonlySet<SyncedTableName> = new Set([
  'card_financial_observations',
  'card_autopay_observations',
  'grace_confirmations',
  'recommendation_results',
]);

/** Fully immutable — insert only; corrections are new rows (docs/08 §9). */
export const IMMUTABLE_TABLES: ReadonlySet<SyncedTableName> = new Set(['import_batches']);

export const ENTITY_SCHEMAS: Record<SyncedTableName, SyncedEntitySchemas> = {
  evidence_sources: {
    remoteRow: evidenceSources.remoteRow,
    remoteInsertPayload: evidenceSources.remoteInsertPayload,
  },
  cards: { remoteRow: cards.remoteRow, remoteInsertPayload: cards.remoteInsertPayload },
  card_financial_observations: {
    remoteRow: cardFinancialObservations.remoteRow,
    remoteInsertPayload: cardFinancialObservations.remoteInsertPayload,
  },
  card_autopay_observations: {
    remoteRow: cardAutopayObservations.remoteRow,
    remoteInsertPayload: cardAutopayObservations.remoteInsertPayload,
  },
  grace_confirmations: {
    remoteRow: graceConfirmations.remoteRow,
    remoteInsertPayload: graceConfirmations.remoteInsertPayload,
  },
  field_assertions: {
    remoteRow: fieldAssertions.remoteRow,
    remoteInsertPayload: fieldAssertions.remoteInsertPayload,
  },
  reward_rules: {
    remoteRow: rewardRules.remoteRow,
    remoteInsertPayload: rewardRules.remoteInsertPayload,
  },
  benefits: { remoteRow: benefits.remoteRow, remoteInsertPayload: benefits.remoteInsertPayload },
  card_fee_schedules: {
    remoteRow: cardFeeSchedules.remoteRow,
    remoteInsertPayload: cardFeeSchedules.remoteInsertPayload,
  },
  merchants: {
    remoteRow: merchants.remoteRow,
    remoteInsertPayload: merchants.remoteInsertPayload,
  },
  offers: { remoteRow: offers.remoteRow, remoteInsertPayload: offers.remoteInsertPayload },
  statement_cycles: {
    remoteRow: statementCycles.remoteRow,
    remoteInsertPayload: statementCycles.remoteInsertPayload,
  },
  purchase_queries: {
    remoteRow: purchaseQueries.remoteRow,
    remoteInsertPayload: purchaseQueries.remoteInsertPayload,
  },
  recommendation_results: {
    remoteRow: recommendationResults.remoteRow,
    remoteInsertPayload: recommendationResults.remoteInsertPayload,
  },
  payment_history_coverage: {
    remoteRow: paymentHistoryCoverage.remoteRow,
    remoteInsertPayload: paymentHistoryCoverage.remoteInsertPayload,
  },
  transactions: {
    remoteRow: transactions.remoteRow,
    remoteInsertPayload: transactions.remoteInsertPayload,
  },
  transaction_splits: {
    remoteRow: transactionSplits.remoteRow,
    remoteInsertPayload: transactionSplits.remoteInsertPayload,
  },
  card_payments: {
    remoteRow: cardPayments.remoteRow,
    remoteInsertPayload: cardPayments.remoteInsertPayload,
  },
  payment_allocations: {
    remoteRow: paymentAllocations.remoteRow,
    remoteInsertPayload: paymentAllocations.remoteInsertPayload,
  },
  cash_reservations: {
    remoteRow: cashReservations.remoteRow,
    remoteInsertPayload: cashReservations.remoteInsertPayload,
  },
  deferred_interest_exposures: {
    remoteRow: deferredInterestExposures.remoteRow,
    remoteInsertPayload: deferredInterestExposures.remoteInsertPayload,
  },
  recurring_charges: {
    remoteRow: recurringCharges.remoteRow,
    remoteInsertPayload: recurringCharges.remoteInsertPayload,
  },
  merchant_category_observations: {
    remoteRow: merchantCategoryObservations.remoteRow,
    remoteInsertPayload: merchantCategoryObservations.remoteInsertPayload,
  },
  review_items: {
    remoteRow: reviewItems.remoteRow,
    remoteInsertPayload: reviewItems.remoteInsertPayload,
  },
  import_batches: {
    remoteRow: importBatches.remoteRow,
    remoteInsertPayload: importBatches.remoteInsertPayload,
  },
  settings: { remoteRow: settings.remoteRow, remoteInsertPayload: settings.remoteInsertPayload },
};
